/**
 * Persisted deadline for the auto-available countdown, stored in sessionStorage so it survives a
 * page refresh (the countdown resumes instead of restarting) but not a genuine logout — logout
 * runs `sessionStorage.clear()` (Workspace.tsx), so the next login starts a fresh grace period.
 * This is what stops an expert from refreshing to reset the timer and sit Offline indefinitely.
 * `clearAutoAvailableDeadline` is also called once the countdown has moved the expert to Available.
 */
const DEADLINE_KEY = "expertHub:autoAvailableDeadline";

/** The persisted deadline as an epoch-ms number, or null if none is stored or it's unreadable. */
export function readAutoAvailableDeadline(): number | null {
    try {
        const raw = globalThis.sessionStorage.getItem(DEADLINE_KEY);
        if (raw == null) return null;
        const value = Number(raw);
        return Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
}

export function writeAutoAvailableDeadline(deadlineMs: number): void {
    try {
        globalThis.sessionStorage.setItem(DEADLINE_KEY, String(deadlineMs));
    } catch {
        // sessionStorage can be unavailable (private mode, storage disabled); degrade gracefully.
    }
}

export function clearAutoAvailableDeadline(): void {
    try {
        globalThis.sessionStorage.removeItem(DEADLINE_KEY);
    } catch {
        // no-op: nothing to clear if storage is unavailable.
    }
}

/**
 * The countdown deadline for this login: the stored one if a countdown is already in flight (so a
 * refresh resumes it), otherwise a fresh `now + durationMs` which is persisted. `createdNewDeadline`
 * is true only when a fresh deadline was created, letting the caller emit the "armed" event exactly
 * once per login rather than on every refresh.
 */
export function resolveCountdownDeadline(durationMs: number): { deadlineMs: number; createdNewDeadline: boolean } {
    const stored = readAutoAvailableDeadline();
    if (stored != null) return { deadlineMs: stored, createdNewDeadline: false };
    const deadlineMs = Date.now() + durationMs;
    writeAutoAvailableDeadline(deadlineMs);
    return { deadlineMs, createdNewDeadline: true };
}
